/**
 * Brand logos — the logos creator report pages show next to each advertiser
 * (culturegenesis.com/creators/{slug}/reports). The list holds every
 * advertiser on a report in the 12-month window plus every Sales registry
 * record, with how the name matches the registry and its logo.
 *
 * A logo lives on the registry record (`advertisers/{key}.logo`), so it shows
 * for every name linked to that record, and only an exact or alias match links
 * one: a loose match never shows a logo. Nothing reaches a client page until
 * someone picks a logo here. Sources are free only: Wikimedia Commons (public
 * domain / CC0), the wordmarks on culturegenesis.com, or an uploaded PNG/JPG.
 *
 * Uploading is the manual path: "Upload logo" (or "Replace") on a linked row,
 * or a PNG/JPG dropped on its plate or in the Find modal. Find searches the
 * free sources, for another name too ("Search for"). "Find logos for missing"
 * asks the finder in batches of 20 and stages each record's top suggestion,
 * unchecked: nothing reaches a client page that nobody looked at. "Use checked
 * suggestions" saves them in batches of 10.
 *
 * Unlinked names are linked with "Same as…" (an alias on an existing record,
 * mediaPlans.manage) or "Add to registry" (a new record, sales.view), and
 * "+ Add brand" adds one on no report yet (sales.view); each goes on to Find,
 * so a brand and its logo are one flow. Search, filter and staged suggestions
 * are this tab's own state — never the dashboards tab's URL params.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Button, EmptyState, RowActionsMenu, SearchIcon, Spinner, StatusPill, Tabs, tableStyles } from '../ui';
import type { StatusVariant } from '../ui';
import ConfirmDialog from '../ConfirmDialog';
import NewAdvertiserDialog from '../Sales/MediaPlans/NewAdvertiserDialog';
import type { AdvertiserRecord, MediaPlanAgency, MediaPlanCategory, NeedsAdvertiser } from '../Sales/MediaPlans/types';
import { getMediaPlanReference, listAdvertisers, updateAdvertiser } from '../../services/mediaPlansApi';
import {
  applyLogoBatch,
  downloadAdvertiserLogoCsv,
  findLogos,
  getRegistryDraft,
  listAdvertiserLogos,
  removeAdvertiserLogo,
  setAdvertiserLogo,
  setLogoBackground,
  uploadAdvertiserLogo,
} from '../../services/advertiserLogosApi';
import { usePermissions } from '../../hooks/usePermissions';
import { DashboardNotify } from '../../hooks/useDashboardToast';
import {
  AddBrandDialog,
  ExistingBrand,
  FindLogoModal,
  FoundLogos,
  InitialsBadge,
  LogoDropTarget,
  LogoPlate,
  SameAsDialog,
  isFileDrag,
} from './BrandLogoParts';
import {
  APPLY_BATCH_SIZE,
  BRAND_LOGO_FILTERS,
  BRAND_LOGO_FILTER_LABELS,
  BrandLogoFilter,
  FIND_BATCH_SIZE,
  LOGO_FILE_ACCEPT,
  chunk,
  filterBrandLogoRows,
  fmtMonth,
  linkedKeyOf,
  logoCsvFileName,
  logoErrorMessage,
  missingLogoTargets,
  namesLinkedTo,
  provenanceOf,
  recordNameOf,
  registryRowOf,
  rowNamed,
  sortBrandLogoRows,
  suggestedRecordOf,
  uploadProblem,
} from './brandLogoModel';
import type { AdvertiserLogo, AdvertiserLogoList, AdvertiserLogoMatch, AdvertiserLogoRow, LogoBackground, LogoCandidate } from './types';

interface BrandLogosTabProps {
  notify: DashboardNotify;
  /**
   * Whether the tab is showing (it stays mounted when hidden). While it is, a
   * file dropped outside a drop target is refused instead of the browser
   * opening it in place of the dashboard.
   */
  active?: boolean;
}

/** A record's staged suggestion: checked ones go out with "Use checked suggestions". */
interface StagedPick {
  candidate: LogoCandidate;
  checked: boolean;
  error?: string;
}

interface BulkRun {
  kind: 'find' | 'apply';
  done: number;
  total: number;
}

const MATCH_PILL: Record<AdvertiserLogoMatch['state'], { variant: StatusVariant; label: string }> = {
  exact: { variant: 'ok', label: 'In registry' },
  alias: { variant: 'approved', label: 'Alias' },
  possible: { variant: 'pending', label: 'Possible match' },
  ambiguous: { variant: 'flagged', label: 'Several matches' },
  none: { variant: 'draft', label: 'Not in registry' },
};

const subLineStyle: React.CSSProperties = { fontSize: 11.5, color: 'var(--fg-4)', fontWeight: 400 };

const visuallyHidden: React.CSSProperties = {
  position: 'absolute',
  width: 1,
  height: 1,
  margin: -1,
  overflow: 'hidden',
  clip: 'rect(0 0 0 0)',
  whiteSpace: 'nowrap',
  border: 0,
};

const plural = (n: number, one: string, many = `${one}s`) => `${n} ${n === 1 ? one : many}`;

/** A batch result for one item: matched by advertiser key, else by position. */
function resultFor<R extends { advertiserKey?: string }>(results: ReadonlyArray<R | undefined>, advertiserKey: string, index: number): R | undefined {
  return results.find((r) => r?.advertiserKey === advertiserKey) ?? results[index];
}

/** Light / Dark plate switch for a saved logo. */
const PlateToggle: React.FC<{ name: string; value: LogoBackground; disabled: boolean; onChange: (b: LogoBackground) => void }> = ({
  name,
  value,
  disabled,
  onChange,
}) => (
  <div role="group" aria-label={`Plate for ${name}`} style={{ display: 'inline-flex', border: '1px solid var(--border-1)', borderRadius: 999, overflow: 'hidden' }}>
    {(['light', 'dark'] as LogoBackground[]).map((b) => (
      <button
        key={b}
        type="button"
        aria-pressed={value === b}
        disabled={disabled}
        onClick={() => value !== b && onChange(b)}
        style={{
          border: 'none',
          cursor: disabled ? 'not-allowed' : 'pointer',
          padding: '5px 11px',
          fontSize: 12,
          fontWeight: 700,
          fontFamily: 'var(--font-sans)',
          background: value === b ? 'var(--accent)' : 'transparent',
          color: value === b ? 'var(--fg-on-accent)' : 'var(--fg-3)',
        }}
      >
        {b === 'light' ? 'Light' : 'Dark'}
      </button>
    ))}
  </div>
);

/** How the name matches the registry, and why no logo can show when it doesn't. */
const MatchCell: React.FC<{ row: AdvertiserLogoRow }> = ({ row }) => {
  const m = row.match;
  const pill = MATCH_PILL[m.state];
  let detail: React.ReactNode = null;
  if (m.state === 'exact' || m.state === 'alias') {
    detail = (
      <>
        {m.state === 'alias' ? <>Alias of {m.recordName}</> : m.recordName !== row.name ? <>As {m.recordName}</> : null}
        {m.normalizedOnly && <div style={{ marginTop: 2 }}>matched by normalized name</div>}
      </>
    );
  } else if (m.state === 'possible') {
    detail = <>Close to {m.recordName} — a loose match never shows a logo</>;
  } else if (m.state === 'ambiguous') {
    detail = <>Matches {m.candidates.map((c) => c.name).join(', ')} — no logo shows until it’s linked to one</>;
  } else if (m.state === 'none' && m.gamIdHint) {
    detail = <>Same GAM advertiser as {m.gamIdHint.name}</>;
  }
  return (
    <div style={{ minWidth: 130, maxWidth: 220 }}>
      <StatusPill variant={pill.variant} size="sm">{pill.label}</StatusPill>
      {detail && <div style={{ ...subLineStyle, marginTop: 4 }}>{detail}</div>}
    </div>
  );
};

const BrandLogosTab: React.FC<BrandLogosTabProps> = ({ notify, active = true }) => {
  const { has } = usePermissions();
  const canAlias = has('mediaPlans.manage');
  const canAddRecord = has('sales.view');

  const [list, setList] = useState<AdvertiserLogoList | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const listSeq = useRef(0);
  const loadedRef = useRef(false);
  /** Bumped by every local write, so a list request that started before one doesn't undo it. */
  const writeSeq = useRef(0);
  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  const [query, setQuery] = useState('');
  const [filter, setFilter] = useState<BrandLogoFilter>('missing');

  /** Finder results and staged suggestions, per registry record. */
  const [found, setFound] = useState<Map<string, FoundLogos>>(() => new Map());
  const [staged, setStaged] = useState<Map<string, StagedPick>>(() => new Map());
  const [bulk, setBulk] = useState<BulkRun | null>(null);
  const stopRef = useRef(false);

  /** Records with a logo write in flight → what the row says meanwhile. */
  const [busy, setBusy] = useState<Map<string, string>>(() => new Map());
  const [findTarget, setFindTarget] = useState<{ row: AdvertiserLogoRow; key: string } | null>(null);
  /** The open Find modal now, for an upload that finishes after it changed. */
  const findTargetRef = useRef(findTarget);
  findTargetRef.current = findTarget;
  const [findLoading, setFindLoading] = useState(false);
  const findSeq = useRef(0);
  const [savingCandidateId, setSavingCandidateId] = useState<string | null>(null);
  const [removeTarget, setRemoveTarget] = useState<{ row: AdvertiserLogoRow; key: string } | null>(null);
  const fileRef = useRef<HTMLInputElement | null>(null);
  const uploadTarget = useRef<{ key: string; name: string } | null>(null);

  const [sameAs, setSameAs] = useState<{ row: AdvertiserLogoRow; initialKey?: string } | null>(null);
  const [registry, setRegistry] = useState<AdvertiserRecord[] | null>(null);
  const [registryLoading, setRegistryLoading] = useState(false);
  const [registryError, setRegistryError] = useState<string | null>(null);
  const [linking, setLinking] = useState(false);
  const [linkError, setLinkError] = useState<string | null>(null);

  const [draftingId, setDraftingId] = useState<string | null>(null);
  const [addTarget, setAddTarget] = useState<{
    row: AdvertiserLogoRow;
    needs: NeedsAdvertiser;
    categories: MediaPlanCategory[];
    agencies: MediaPlanAgency[];
  } | null>(null);

  /** "+ Add brand": the name prompt, then the registry dialog on its draft. */
  const [addBrandOpen, setAddBrandOpen] = useState(false);
  const [brandDrafting, setBrandDrafting] = useState(false);
  const [brandError, setBrandError] = useState<string | null>(null);
  const [brandDraft, setBrandDraft] = useState<{
    needs: NeedsAdvertiser;
    categories: MediaPlanCategory[];
    agencies: MediaPlanAgency[];
  } | null>(null);

  // A file dropped beside a target would otherwise open in the tab, losing staged work.
  useEffect(() => {
    if (!active) return undefined;
    const refuse = (e: DragEvent) => {
      if (e.defaultPrevented || !isFileDrag(e)) return;
      if ((e.target as Element | null)?.closest?.('input[type="file"]')) return;
      e.preventDefault();
      if (e.dataTransfer) e.dataTransfer.dropEffect = 'none';
    };
    window.addEventListener('dragover', refuse);
    window.addEventListener('drop', refuse);
    return () => {
      window.removeEventListener('dragover', refuse);
      window.removeEventListener('drop', refuse);
    };
  }, [active]);

  /**
   * Reload without blanking the table. Only the newest request may set state,
   * and a response that started before a local write is dropped for a fresh
   * request. A failure before anything loaded shows an error card; after that
   * it's a toast over the last list.
   */
  const refresh = useCallback(() => {
    const seq = ++listSeq.current;
    const writesAtStart = writeSeq.current;
    setLoading(true);
    listAdvertiserLogos()
      .then((r) => {
        if (seq !== listSeq.current) return;
        if (writeSeq.current !== writesAtStart) {
          refreshRef.current();
          return;
        }
        setList(r);
        setLoadError(null);
        loadedRef.current = true;
      })
      .catch((err) => {
        if (seq !== listSeq.current) return;
        const message = err?.message || 'Unknown error';
        if (loadedRef.current) notify('error', 'Couldn’t refresh brand logos', message);
        else setLoadError(message);
      })
      .finally(() => {
        if (seq === listSeq.current) setLoading(false);
      });
  }, [notify]);
  const refreshRef = useRef(refresh);
  refreshRef.current = refresh;

  useEffect(() => { refresh(); }, [refresh]);

  const sortedRows = useMemo(() => sortBrandLogoRows(list?.rows ?? []), [list]);
  const { rows, counts } = useMemo(() => filterBrandLogoRows(sortedRows, { q: query, filter }), [sortedRows, query, filter]);
  const logoByKey = useMemo(() => {
    const map = new Map<string, AdvertiserLogo>();
    for (const row of sortedRows) {
      const key = linkedKeyOf(row);
      if (key && row.logo) map.set(key, row.logo);
    }
    return map;
  }, [sortedRows]);

  // --- local writes ---------------------------------------------------------------

  const patchRows = (fn: (row: AdvertiserLogoRow) => AdvertiserLogoRow) => {
    writeSeq.current += 1;
    setList((prev) => (prev ? { ...prev, rows: prev.rows.map(fn) } : prev));
  };

  /** A logo belongs to the record, so every name linked to it changes together. */
  const setLogoFor = (key: string, logo: AdvertiserLogo | null) => {
    patchRows((row) => (linkedKeyOf(row) === key ? { ...row, logo } : row));
    if (logo) {
      setStaged((prev) => {
        if (!prev.has(key)) return prev;
        const next = new Map(prev);
        next.delete(key);
        return next;
      });
    }
  };

  /** Link one row to a record locally (the refresh that follows has the server's word). */
  const linkRow = (rowId: string, match: AdvertiserLogoMatch & { key: string }) => {
    const logo = logoByKey.get(match.key) ?? null;
    patchRows((row) => (row.id === rowId ? { ...row, match, logo } : row));
  };

  const setRowBusy = (key: string, label: string | null) =>
    setBusy((prev) => {
      const next = new Map(prev);
      if (label) next.set(key, label);
      else next.delete(key);
      return next;
    });

  // --- find + stage + apply ---------------------------------------------------------

  const findSkip = useMemo(() => {
    const skip = new Set<string>(staged.keys());
    found.forEach((f, key) => { if (!f.error) skip.add(key); });
    return skip;
  }, [found, staged]);
  const findTargets = useMemo(() => missingLogoTargets(sortedRows, findSkip), [sortedRows, findSkip]);

  const recordFound = (
    items: Array<{ name: string; advertiserKey: string }>,
    results: Array<{ advertiserKey?: string; candidates: LogoCandidate[]; error?: string } | undefined>,
  ) => {
    setFound((prev) => {
      const next = new Map(prev);
      items.forEach((item, i) => {
        const r = resultFor(results, item.advertiserKey, i);
        next.set(
          item.advertiserKey,
          r
            ? { candidates: r.candidates ?? [], error: r.error, searchedFor: item.name }
            : { candidates: [], error: 'No result came back for this advertiser.', searchedFor: item.name },
        );
      });
      return next;
    });
  };

  const findMissing = async () => {
    if (bulk || findTargets.length === 0) return;
    const targets = findTargets;
    stopRef.current = false;
    setBulk({ kind: 'find', done: 0, total: targets.length });
    let done = 0;
    let suggested = 0;
    let failed = 0;
    try {
      for (const part of chunk(targets, FIND_BATCH_SIZE)) {
        if (stopRef.current || !mountedRef.current) break;
        const results = await findLogos(part);
        recordFound(part, results);
        const picks = new Map<string, LogoCandidate>();
        for (let i = 0; i < part.length; i += 1) {
          const r = resultFor(results, part[i].advertiserKey, i);
          if (!r || r.error) failed += 1;
          else if (r.candidates.length > 0) picks.set(part[i].advertiserKey, r.candidates[0]);
        }
        suggested += picks.size;
        // Unchecked: a top pick can be the wrong brand ("React" the JavaScript library), so a person checks each one.
        setStaged((prev) => {
          const next = new Map(prev);
          picks.forEach((candidate, key) => { if (!next.has(key)) next.set(key, { candidate, checked: false }); });
          return next;
        });
        done += part.length;
        setBulk({ kind: 'find', done, total: targets.length });
      }
    } catch (err: any) {
      notify('error', 'Finding logos stopped', `${err?.message || 'The search failed.'} ${done} of ${targets.length} were searched — run it again to continue.`);
      setBulk(null);
      return;
    }
    setBulk(null);
    if (!mountedRef.current) return;
    const stopped = done < targets.length;
    const failedNote = failed > 0 ? ` ${plural(failed, 'search', 'searches')} failed — run Find logos again to retry.` : '';
    notify(
      suggested > 0 || failed === 0 ? 'success' : 'error',
      stopped ? `Stopped after ${done} of ${targets.length}` : `Suggestions for ${suggested} of ${plural(targets.length, 'advertiser')}`,
      (suggested > 0
        ? 'Check the ones that are the right brand in the Suggestion column, then use the checked suggestions.'
        : 'No free logos found — upload them instead.') + failedNote,
    );
  };

  const checkedPicks = useMemo(
    () => Array.from(staged.entries()).filter(([, s]) => s.checked).map(([advertiserKey, s]) => ({ advertiserKey, source: s.candidate.source, ref: s.candidate.ref })),
    [staged],
  );

  const applyChecked = async () => {
    if (bulk || checkedPicks.length === 0) return;
    const picks = checkedPicks;
    setBulk({ kind: 'apply', done: 0, total: picks.length });
    let done = 0;
    let saved = 0;
    let failed = 0;
    try {
      for (const part of chunk(picks, APPLY_BATCH_SIZE)) {
        if (!mountedRef.current) break;
        const results = await applyLogoBatch(part);
        const errors = new Map<string, string>();
        for (const pick of part) {
          const r = results.find((x) => x.advertiserKey === pick.advertiserKey);
          if (r?.ok) {
            setLogoFor(pick.advertiserKey, r.logo);
            saved += 1;
          } else {
            errors.set(pick.advertiserKey, r ? logoErrorMessage({ code: r.code, message: r.error }) : 'No result came back for this logo.');
            failed += 1;
          }
        }
        if (errors.size > 0) {
          // Unchecked so a retry is a deliberate re-check, never a silent loop.
          setStaged((prev) => {
            const next = new Map(prev);
            errors.forEach((error, key) => {
              const s = next.get(key);
              if (s) next.set(key, { ...s, checked: false, error });
            });
            return next;
          });
        }
        done += part.length;
        setBulk({ kind: 'apply', done, total: picks.length });
      }
    } catch (err: any) {
      notify('error', 'Saving logos stopped', `${err?.message || 'The request failed.'} ${saved} saved before it stopped.`);
      setBulk(null);
      return;
    }
    setBulk(null);
    if (saved === 0) notify('error', 'No logos saved', 'See the Suggestion column for what went wrong.');
    else notify('success', `${plural(saved, 'logo')} saved`, failed > 0 ? `${failed} couldn’t be saved — see the Suggestion column.` : 'Creator report pages show them within about a minute.');
  };

  const toggleStaged = (key: string, checked: boolean) =>
    setStaged((prev) => {
      const s = prev.get(key);
      if (!s) return prev;
      return new Map(prev).set(key, { ...s, checked, error: checked ? undefined : s.error });
    });

  const shownStagedKeys = useMemo(() => {
    const keys = new Set<string>();
    rows.forEach((row) => {
      const key = linkedKeyOf(row);
      if (key && !row.logo && staged.has(key)) keys.add(key);
    });
    return Array.from(keys);
  }, [rows, staged]);
  const shownChecked = shownStagedKeys.filter((k) => staged.get(k)?.checked).length;
  const toggleAllShown = (checked: boolean) =>
    setStaged((prev) => {
      const next = new Map(prev);
      shownStagedKeys.forEach((k) => {
        const s = next.get(k);
        if (s) next.set(k, { ...s, checked, error: checked ? undefined : s.error });
      });
      return next;
    });

  // --- one record: find, use, upload, plate, remove ---------------------------------

  /** Ask the finder for one record — under the row's name, or a name someone typed in Find. */
  const loadCandidates = async (row: AdvertiserLogoRow, key: string, name: string = row.name) => {
    const seq = ++findSeq.current;
    setFindLoading(true);
    try {
      const results = await findLogos([{ name, advertiserKey: key }]);
      if (seq === findSeq.current) recordFound([{ name, advertiserKey: key }], results);
    } catch (err: any) {
      if (seq === findSeq.current) {
        setFound((prev) => new Map(prev).set(key, { candidates: [], error: err?.message || 'The search failed.', searchedFor: name }));
      }
    } finally {
      if (seq === findSeq.current) setFindLoading(false);
    }
  };

  const openFind = (row: AdvertiserLogoRow, key: string) => {
    setFindTarget({ row, key });
    const cached = found.get(key);
    if (!cached || cached.error) void loadCandidates(row, key, cached?.searchedFor);
    else setFindLoading(false);
  };

  const closeFind = () => {
    findSeq.current += 1;
    setFindLoading(false);
    setFindTarget(null);
  };

  const pickCandidate = async (key: string, recordName: string, candidate: LogoCandidate) => {
    setSavingCandidateId(candidate.id);
    try {
      const logo = await setAdvertiserLogo(key, { source: candidate.source, ref: candidate.ref });
      setLogoFor(key, logo);
      closeFind();
      notify('success', `Logo saved for ${recordName}`, 'Creator report pages show it within about a minute.');
    } catch (err: any) {
      notify('error', 'Couldn’t save that logo', logoErrorMessage(err));
    } finally {
      setSavingCandidateId(null);
    }
  };

  const startUpload = (key: string, name: string) => {
    uploadTarget.current = { key, name };
    fileRef.current?.click();
  };

  /** Picked or dropped — either way, checked here first, then trimmed and stored by the server. */
  const uploadFile = async (target: { key: string; name: string }, file: File) => {
    const problem = uploadProblem(file);
    if (problem) {
      notify('error', 'Can’t use that file', problem);
      return;
    }
    setRowBusy(target.key, 'Uploading…');
    try {
      const logo = await uploadAdvertiserLogo(target.key, file);
      setLogoFor(target.key, logo);
      if (findTargetRef.current?.key === target.key) closeFind();
      notify('success', `Logo uploaded for ${target.name}`, 'Creator report pages show it within about a minute.');
    } catch (err: any) {
      notify('error', 'Upload failed', logoErrorMessage(err));
    } finally {
      setRowBusy(target.key, null);
    }
  };

  const onFilePicked = (file: File | undefined) => {
    const target = uploadTarget.current;
    if (fileRef.current) fileRef.current.value = ''; // re-picking the same file must fire onChange again
    if (file && target) void uploadFile(target, file);
  };

  const onFilesDropped = (target: { key: string; name: string }, files: File[]) => {
    if (files.length === 0) return;
    if (files.length > 1) {
      notify('error', 'Drop one file at a time', 'An advertiser has one logo — drop a single PNG or JPG.');
      return;
    }
    void uploadFile(target, files[0]);
  };

  const changePlate = async (key: string, background: LogoBackground) => {
    setRowBusy(key, 'Saving…');
    try {
      setLogoFor(key, await setLogoBackground(key, background));
    } catch (err: any) {
      notify('error', 'Couldn’t change the plate', logoErrorMessage(err));
    } finally {
      setRowBusy(key, null);
    }
  };

  const runRemove = async () => {
    const target = removeTarget;
    if (!target) return;
    setRemoveTarget(null);
    setRowBusy(target.key, 'Removing…');
    try {
      await removeAdvertiserLogo(target.key);
      setLogoFor(target.key, null);
      notify('success', 'Logo removed', 'Creator report pages go back to initials within about a minute.');
    } catch (err: any) {
      notify('error', 'Couldn’t remove the logo', logoErrorMessage(err));
    } finally {
      setRowBusy(target.key, null);
    }
  };

  // --- linking: Same as… and Add to registry ---------------------------------------

  const loadRegistry = async () => {
    setRegistryLoading(true);
    setRegistryError(null);
    try {
      setRegistry(await listAdvertisers());
    } catch (err: any) {
      setRegistryError(err?.message || 'Unknown error');
    } finally {
      setRegistryLoading(false);
    }
  };

  const openSameAs = (row: AdvertiserLogoRow, initialKey?: string) => {
    setSameAs({ row, initialKey });
    setLinkError(null);
    if (!registry && !registryLoading) void loadRegistry();
  };

  const linkAsAlias = async (record: AdvertiserRecord) => {
    const row = sameAs?.row;
    if (!row) return;
    setLinking(true);
    setLinkError(null);
    try {
      const updated = await updateAdvertiser(record.key, { addAliases: [row.name] });
      setRegistry((prev) => prev?.map((a) => (a.key === updated.key ? updated : a)) ?? prev);
      const match = { state: 'alias' as const, key: updated.key, recordName: updated.name, normalizedOnly: false };
      linkRow(row.id, match);
      setSameAs(null);
      const hasLogo = logoByKey.has(updated.key);
      notify(
        'success',
        `“${row.name}” now matches ${updated.name}`,
        hasLogo ? 'Its logo shows on report pages within about a minute.' : 'Find or upload its logo next.',
      );
      refresh();
      // Linking was for the logo: go straight on to it, unless the record already has one.
      if (!hasLogo) openFind({ ...row, match, logo: null }, updated.key);
    } catch (err: any) {
      setLinkError(err?.status === 403 ? 'Linking a name needs the Media Plans · Manage Rate Cards permission.' : err?.message || 'Couldn’t link the name.');
    } finally {
      setLinking(false);
    }
  };

  const openAddToRegistry = async (row: AdvertiserLogoRow) => {
    setDraftingId(row.id);
    try {
      const [needs, reference] = await Promise.all([
        getRegistryDraft(row.name, row.gamAdvertiserId),
        // Labels, descriptions and agencies for the form; the draft's own category list covers a failure.
        getMediaPlanReference().catch(() => null),
      ]);
      if (!mountedRef.current) return;
      setAddTarget({ row, needs, categories: reference?.categories.categories ?? [], agencies: reference?.agencies.agencies ?? [] });
    } catch (err: any) {
      notify('error', 'Couldn’t start the registry entry', err?.message);
    } finally {
      setDraftingId(null);
    }
  };

  const recordCreated = (record: AdvertiserRecord) => {
    const row = addTarget?.row;
    setAddTarget(null);
    if (!row) return;
    setRegistry((prev) => (prev ? [...prev, record] : prev));
    const match = { state: record.name === row.name ? ('exact' as const) : ('alias' as const), key: record.key, recordName: record.name, normalizedOnly: false };
    linkRow(row.id, match);
    notify('success', `${record.name} added to the registry`, 'Find or upload its logo next.');
    refresh();
    openFind({ ...row, match, logo: null }, record.key);
  };

  /** "Use {name}" in the registry dialog: link to that record instead of creating one. */
  const linkExistingRecord = (key: string, name: string) => {
    const row = addTarget?.row;
    setAddTarget(null);
    if (!row) return;
    if (!canAlias) {
      notify('error', 'Linking needs the Media Plans · Manage Rate Cards permission', `Ask someone who has it to add “${row.name}” as an alias of ${name}.`);
      return;
    }
    openSameAs(row, key);
  };

  // --- + Add brand: one on no report and not in the registry yet -----------------------

  /** What a typed name already is in the list, so the brand isn't added twice. */
  const existingBrand = (name: string): (ExistingBrand & { row: AdvertiserLogoRow }) | null => {
    const row = rowNamed(sortedRows, name);
    if (!row) return null;
    const recordName = recordNameOf(row);
    if (linkedKeyOf(row)) {
      return {
        row,
        message: row.name === recordName ? `${recordName} is already in the registry.` : `“${row.name}” is already linked to ${recordName} in the registry.`,
        actionLabel: 'Find or upload its logo',
      };
    }
    if (row.match.state === 'ambiguous') {
      return {
        row,
        message: `Creator reports already show “${row.name}”, and it matches several registry advertisers. Link it to the right one with Same as… on its row.`,
        actionLabel: 'Show its row',
      };
    }
    return {
      row,
      message: `Creator reports already show “${row.name}” — add that name to the registry, so it keeps matching the reports.`,
      actionLabel: 'Add it to the registry',
    };
  };

  const goToExistingBrand = (name: string) => {
    const existing = existingBrand(name);
    setAddBrandOpen(false);
    if (!existing) return;
    const { row } = existing;
    const key = linkedKeyOf(row);
    if (key) {
      openFind(row, key);
    } else if (row.match.state === 'ambiguous') {
      setQuery(row.name);
      setFilter('unlinked');
    } else {
      void openAddToRegistry(row);
    }
  };

  const startAddBrand = async (name: string) => {
    setBrandDrafting(true);
    setBrandError(null);
    try {
      const [needs, reference] = await Promise.all([getRegistryDraft(name), getMediaPlanReference().catch(() => null)]);
      if (!mountedRef.current) return;
      setAddBrandOpen(false);
      setBrandDraft({ needs, categories: reference?.categories.categories ?? [], agencies: reference?.agencies.agencies ?? [] });
    } catch (err: any) {
      setBrandError(err?.message || 'Couldn’t start the registry entry.');
    } finally {
      setBrandDrafting(false);
    }
  };

  /** A registry-only row until the brand runs on a report; its logo comes next. */
  const brandCreated = (record: AdvertiserRecord) => {
    setBrandDraft(null);
    setRegistry((prev) => (prev ? [...prev, record] : prev));
    const row = registryRowOf(record.key, record.name);
    writeSeq.current += 1;
    setList((prev) => (prev && !prev.rows.some((r) => linkedKeyOf(r) === record.key) ? { ...prev, rows: [...prev.rows, row] } : prev));
    notify('success', `${record.name} added to the registry`, 'Upload or pick its logo next.');
    refresh();
    openFind(row, record.key);
  };

  /** "Use {name}" on a duplicate the server found: that record's logo, not a second record. */
  const openExistingRecord = (key: string, name: string) => {
    setBrandDraft(null);
    openFind(sortedRows.find((r) => linkedKeyOf(r) === key) ?? registryRowOf(key, name), key);
  };

  // --- render -------------------------------------------------------------------------

  const download = async () => {
    try {
      await downloadAdvertiserLogoCsv(logoCsvFileName());
    } catch (err: any) {
      notify('error', 'Export failed', err?.message);
    }
  };

  const applying = bulk?.kind === 'apply';

  const renderSuggestion = (row: AdvertiserLogoRow, key: string | null) => {
    if (!key || row.logo) return null;
    const s = staged.get(key);
    if (s) {
      // What the finder matched (Wikidata's label and description) sits beside the box, so a wrong brand is caught before it's checked.
      return (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 220, maxWidth: 300 }}>
          <LogoPlate src={s.candidate.previewUrl} background="light" alt="" width={96} height={40} />
          <div style={{ minWidth: 0 }}>
            <div style={{ fontWeight: 600, overflowWrap: 'anywhere' }}>{s.candidate.label}</div>
            {s.candidate.description && (
              <div
                title={s.candidate.description}
                style={{ ...subLineStyle, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}
              >
                {s.candidate.description}
              </div>
            )}
            <div style={subLineStyle}>
              {provenanceOf(s.candidate.source, s.candidate.license)} ·{' '}
              <button
                type="button"
                onClick={() => openFind(row, key)}
                style={{ border: 'none', background: 'none', padding: 0, color: 'var(--accent)', fontSize: 11.5, fontWeight: 600, cursor: 'pointer' }}
              >
                Other logos…
              </button>
            </div>
            {s.error && <div style={{ ...subLineStyle, color: 'var(--danger)', marginTop: 2 }}>{s.error}</div>}
          </div>
        </div>
      );
    }
    const f = found.get(key);
    if (f?.error) return <span style={{ ...subLineStyle, color: 'var(--danger)' }}>Search failed — try Find again</span>;
    if (f && f.candidates.length === 0) return <span style={subLineStyle}>No free logo found — upload one</span>;
    return null;
  };

  const renderActions = (row: AdvertiserLogoRow, key: string | null) => {
    if (!key) {
      const suggestion = suggestedRecordOf(row);
      const canAdd = canAddRecord && row.match.state !== 'ambiguous';
      if (!canAlias && !canAdd) {
        return (
          <span style={subLineStyle} title="“Same as…” needs Media Plans · Manage Rate Cards; “Add to registry” needs Sales.">
            Linking needs Sales access
          </span>
        );
      }
      return (
        <>
          {canAlias && (
            <Button size="sm" variant={suggestion ? 'subtle' : 'ghost'} onClick={() => openSameAs(row)} aria-label={`Same as… for ${row.name}`}>
              Same as…
            </Button>
          )}
          {canAdd && (
            <Button size="sm" variant="ghost" onClick={() => openAddToRegistry(row)} disabled={draftingId !== null} aria-label={`Add ${row.name} to the registry`}>
              {draftingId === row.id ? 'Preparing…' : 'Add to registry'}
            </Button>
          )}
        </>
      );
    }
    const busyLabel = busy.get(key);
    const disabled = !!busyLabel || applying;
    return (
      <>
        {busyLabel && <span style={subLineStyle} aria-live="polite">{busyLabel}</span>}
        {row.logo ? (
          <>
            <PlateToggle name={row.name} value={row.logo.background} disabled={disabled} onChange={(b) => changePlate(key, b)} />
            <Button size="sm" variant="ghost" onClick={() => startUpload(key, row.name)} disabled={disabled} aria-label={`Replace the logo for ${row.name} with a file`}>
              Replace
            </Button>
            <RowActionsMenu
              label={row.name}
              actions={[
                { label: 'Find a free logo…', onSelect: () => openFind(row, key) },
                { label: 'Remove logo…', danger: true, onSelect: () => setRemoveTarget({ row, key }) },
              ]}
            />
          </>
        ) : (
          <>
            <Button size="sm" variant="subtle" onClick={() => openFind(row, key)} disabled={disabled} aria-label={`Find a logo for ${row.name}`}>
              Find
            </Button>
            <Button size="sm" variant="ghost" onClick={() => startUpload(key, row.name)} disabled={disabled} aria-label={`Upload a logo for ${row.name}`}>
              Upload logo
            </Button>
          </>
        )}
      </>
    );
  };

  const renderRow = (row: AdvertiserLogoRow) => {
    const key = linkedKeyOf(row);
    const s = key && !row.logo ? staged.get(key) : undefined;
    return (
      <tr key={row.id}>
        <td style={{ ...tableStyles.td, width: 36, paddingRight: 0 }}>
          {s && key && (
            <input
              type="checkbox"
              checked={s.checked}
              disabled={applying}
              onChange={(e) => toggleStaged(key, e.target.checked)}
              aria-label={`Use the suggested logo for ${row.name}`}
              style={{ cursor: 'pointer' }}
            />
          )}
        </td>
        <td style={{ ...tableStyles.td, width: 128 }}>
          {key ? (
            <LogoDropTarget name={row.name} disabled={!!busy.get(key) || applying} onFiles={(files) => onFilesDropped({ key, name: row.name }, files)}>
              {row.logo ? (
                <LogoPlate src={row.logo.url} background={row.logo.background} alt={`${row.name} logo`} width={112} height={44} />
              ) : (
                <InitialsBadge name={row.name} />
              )}
            </LogoDropTarget>
          ) : (
            <InitialsBadge name={row.name} />
          )}
        </td>
        <td style={{ ...tableStyles.td, minWidth: 170, maxWidth: 260 }}>
          <div style={{ fontWeight: 600, overflowWrap: 'anywhere' }}>{row.name}</div>
          <div style={{ ...subLineStyle, marginTop: 3 }}>
            {row.inReports
              ? row.lastMonth
                ? `On reports through ${fmtMonth(row.lastMonth)}`
                : 'On reports'
              : 'Registry only — not on a report in this window'}
          </div>
        </td>
        <td style={tableStyles.tdNum}>{row.channelCount ?? '—'}</td>
        <td style={tableStyles.td}>
          <MatchCell row={row} />
        </td>
        <td style={tableStyles.td}>{renderSuggestion(row, key)}</td>
        <td style={{ ...tableStyles.td, whiteSpace: 'nowrap' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 6 }}>{renderActions(row, key)}</div>
        </td>
      </tr>
    );
  };

  const findRecordName = findTarget ? recordNameOf(findTarget.row) : '';
  const total = sortedRows.length;
  const filtered = query.trim() !== '' || filter !== 'all';

  const renderList = () => {
    if (!list) {
      if (loadError) {
        return (
          <div
            role="alert"
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 12,
              flexWrap: 'wrap',
              padding: '14px 16px',
              borderRadius: 'var(--radius-lg)',
              background: 'var(--danger-soft)',
              border: '1px solid var(--border-1)',
              fontSize: 13,
            }}
          >
            <div>
              <strong>Couldn’t load brand logos.</strong> <span style={{ color: 'var(--fg-3)' }}>{loadError}</span>
            </div>
            <Button size="sm" variant="ghost" onClick={refresh} disabled={loading}>
              {loading ? 'Retrying…' : 'Retry'}
            </Button>
          </div>
        );
      }
      return <div style={{ display: 'flex', justifyContent: 'center', padding: 50 }}><Spinner /></div>;
    }
    if (total === 0) {
      return (
        <EmptyState
          title="No advertisers yet"
          subtitle="Advertisers show up here once a Direct Sold campaign runs on a creator’s channel, or once they’re added to the Sales registry."
        />
      );
    }
    return (
      <>
        <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 10 }}>
          <div style={{ position: 'relative', flex: '1 1 260px', minWidth: 200, maxWidth: 420 }}>
            <span style={{ position: 'absolute', left: 11, top: '50%', transform: 'translateY(-50%)', color: 'var(--fg-4)', display: 'inline-flex' }}>
              <SearchIcon />
            </span>
            <input
              type="text"
              role="searchbox"
              aria-label="Search advertisers"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Escape' && query) {
                  e.preventDefault();
                  setQuery('');
                }
              }}
              placeholder="Search advertisers or registry names…"
              style={{
                width: '100%',
                boxSizing: 'border-box',
                height: 36,
                padding: '0 34px 0 32px',
                borderRadius: 'var(--radius-md)',
                border: '1px solid var(--border-1)',
                background: 'var(--bg-surface-1)',
                fontSize: 13.5,
                fontFamily: 'var(--font-sans)',
                color: 'var(--fg-1)',
              }}
            />
            {query && (
              <button
                type="button"
                aria-label="Clear search"
                onClick={() => setQuery('')}
                style={{
                  position: 'absolute',
                  right: 4,
                  top: '50%',
                  transform: 'translateY(-50%)',
                  width: 28,
                  height: 28,
                  border: 'none',
                  borderRadius: 'var(--radius-sm)',
                  background: 'transparent',
                  color: 'var(--fg-3)',
                  cursor: 'pointer',
                  fontSize: 13,
                }}
              >
                ✕
              </button>
            )}
          </div>

          {/* Four filters with counts outgrow a phone; the strip scrolls instead of the page. */}
          <div style={{ maxWidth: '100%', overflowX: 'auto' }}>
            <Tabs
              value={filter}
              onChange={setFilter}
              size="sm"
              ariaLabel="Logo status"
              options={BRAND_LOGO_FILTERS.map((f) => ({ value: f, label: BRAND_LOGO_FILTER_LABELS[f], count: counts[f] }))}
            />
          </div>

          <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 10, fontSize: 12.5, color: 'var(--fg-4)' }}>
            {loading && <span aria-live="polite">Refreshing…</span>}
            <span aria-live="polite">{filtered ? `Showing ${rows.length} of ${total}` : plural(total, 'advertiser')}</span>
          </div>
        </div>

        <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 8 }}>
          <Button
            size="sm"
            variant="subtle"
            onClick={findMissing}
            disabled={!!bulk || findTargets.length === 0}
            title={findTargets.length === 0 ? 'Every advertiser missing a logo has been searched — use Find on a row to search again.' : undefined}
          >
            {findTargets.length > 0 ? `Find logos for missing (${findTargets.length})` : 'Find logos for missing'}
          </Button>
          <Button size="sm" variant="primary" onClick={applyChecked} disabled={!!bulk || checkedPicks.length === 0}>
            Use checked suggestions ({checkedPicks.length})
          </Button>
          {bulk && (
            <span role="status" style={{ display: 'inline-flex', alignItems: 'center', gap: 8, fontSize: 12.5, color: 'var(--fg-3)' }}>
              <span aria-hidden="true" style={{ display: 'inline-flex' }}><Spinner size={14} /></span>
              {bulk.kind === 'find' ? 'Finding logos…' : 'Saving logos…'} {bulk.done} of {bulk.total}
            </span>
          )}
          {bulk?.kind === 'find' && (
            <Button size="sm" variant="ghost" onClick={() => { stopRef.current = true; }}>
              Stop
            </Button>
          )}
          <span style={{ marginLeft: 'auto', display: 'inline-flex', gap: 8, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
            {canAddRecord && (
              <Button
                size="sm"
                variant="ghost"
                onClick={() => { setBrandError(null); setAddBrandOpen(true); }}
                title="A brand that isn’t on a creator report or in the Sales registry yet"
              >
                + Add brand
              </Button>
            )}
            <Button size="sm" variant="ghost" onClick={download}>
              Export list (CSV)
            </Button>
          </span>
        </div>

        {rows.length === 0 ? (
          <div
            style={{
              padding: '28px 16px',
              textAlign: 'center',
              border: '1px dashed var(--border-1)',
              borderRadius: 'var(--radius-lg)',
              color: 'var(--fg-3)',
              fontSize: 13,
            }}
          >
            {query.trim()
              ? <>No advertisers match “{query.trim()}”{filter !== 'all' ? ` under ${BRAND_LOGO_FILTER_LABELS[filter]}` : ''}.</>
              : filter === 'missing'
                ? 'Every advertiser linked to the registry has a logo.'
                : `Nothing under ${BRAND_LOGO_FILTER_LABELS[filter]}.`}{' '}
            <Button size="sm" variant="ghost" onClick={() => { setQuery(''); setFilter('all'); }} style={{ marginLeft: 6 }}>
              Show all
            </Button>
          </div>
        ) : (
          // Positioned so the visually hidden header labels (position: absolute) scroll
          // and clip with the table; unpositioned, they widened the page on a phone.
          <div style={{ ...tableStyles.wrapScroll, position: 'relative' }}>
            <table style={tableStyles.table}>
              <thead>
                <tr>
                  <th style={{ ...tableStyles.th, width: 36, paddingRight: 0 }}>
                    {shownStagedKeys.length > 0 ? (
                      <input
                        type="checkbox"
                        checked={shownChecked === shownStagedKeys.length}
                        ref={(el) => { if (el) el.indeterminate = shownChecked > 0 && shownChecked < shownStagedKeys.length; }}
                        disabled={applying}
                        onChange={(e) => toggleAllShown(e.target.checked)}
                        aria-label="Use every suggestion shown"
                        style={{ cursor: 'pointer' }}
                      />
                    ) : (
                      <span style={visuallyHidden}>Use suggestion</span>
                    )}
                  </th>
                  <th style={tableStyles.th}>Logo</th>
                  <th style={tableStyles.th}>Advertiser</th>
                  <th
                    style={{ ...tableStyles.thNum, whiteSpace: 'normal', minWidth: 96 }}
                    title="How many network channels ran the brand in its busiest month of the report window."
                  >
                    {/* Two lines at most when the table is squeezed onto a phone. */}
                    <span style={{ whiteSpace: 'nowrap' }}>Network channels</span> <span style={{ whiteSpace: 'nowrap' }}>(peak month)</span>
                  </th>
                  <th style={tableStyles.th}>Registry match</th>
                  <th style={tableStyles.th}>Suggestion</th>
                  <th style={tableStyles.th}><span style={visuallyHidden}>Actions</span></th>
                </tr>
              </thead>
              <tbody>{rows.map(renderRow)}</tbody>
            </table>
          </div>
        )}
      </>
    );
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      <div style={{ fontSize: 13, color: 'var(--fg-3)' }}>
        Logos for the advertisers on creator report pages
        {list ? ` (${fmtMonth(list.window.fromMonth)} – ${fmtMonth(list.window.toMonth)})` : ''}, and every advertiser in the
        Sales registry. A logo shows on a client page only after it’s picked here, and only for names linked to a registry
        advertiser. Free sources only: Wikimedia Commons, culturegenesis.com, or your own upload.
      </div>

      {list && list.truncatedMonths.length > 0 && (
        <div
          role="status"
          style={{
            background: 'var(--warning-soft)',
            border: '1px solid var(--border-1)',
            borderRadius: 'var(--radius-md)',
            padding: '10px 14px',
            fontSize: 13,
          }}
        >
          <strong>This list may be incomplete.</strong>{' '}
          <span style={{ color: 'var(--fg-3)' }}>
            The network rollup for {list.truncatedMonths.map(fmtMonth).join(', ')} was too large to read in full, so a brand that
            ran only then may be missing and channel counts may be low.
          </span>
        </div>
      )}

      {renderList()}

      <input
        ref={fileRef}
        type="file"
        accept={LOGO_FILE_ACCEPT}
        aria-label="Logo file"
        style={{ display: 'none' }}
        onChange={(e) => onFilePicked(e.target.files?.[0])}
      />

      {findTarget && (
        <FindLogoModal
          key={findTarget.key}
          rowName={findTarget.row.name}
          recordName={findRecordName}
          linkedNames={namesLinkedTo(sortedRows, findTarget.key)}
          currentLogo={logoByKey.get(findTarget.key) ?? null}
          found={found.get(findTarget.key)}
          loading={findLoading}
          stagedId={staged.get(findTarget.key)?.candidate.id}
          savingId={savingCandidateId}
          uploading={busy.get(findTarget.key) === 'Uploading…'}
          onRetry={() => loadCandidates(findTarget.row, findTarget.key, found.get(findTarget.key)?.searchedFor)}
          onSearch={(name) => loadCandidates(findTarget.row, findTarget.key, name)}
          onUse={(c) => pickCandidate(findTarget.key, findRecordName, c)}
          onUpload={() => startUpload(findTarget.key, findTarget.row.name)}
          onFiles={(files) => onFilesDropped({ key: findTarget.key, name: findTarget.row.name }, files)}
          onClose={closeFind}
        />
      )}

      {addBrandOpen && (
        <AddBrandDialog
          busy={brandDrafting}
          error={brandError}
          existingFor={existingBrand}
          onContinue={startAddBrand}
          onUseExisting={goToExistingBrand}
          onClose={() => setAddBrandOpen(false)}
        />
      )}

      <NewAdvertiserDialog
        state={brandDraft ? { kind: 'needsAdvertiser', needs: brandDraft.needs } : null}
        categories={brandDraft?.categories ?? []}
        agencies={brandDraft?.agencies ?? []}
        eyebrow="Brand logos · Add brand"
        intro={
          brandDraft ? (
            <>
              “{brandDraft.needs.name}” isn’t on a creator report or in the Sales registry yet. Add it so it can have a logo before
              its first month lands. Confirm the industry category too — Sales uses it for rate cards and suggested rates.
            </>
          ) : undefined
        }
        submitLabel="Add brand"
        editableName
        keepOpenedName={false}
        onCreated={brandCreated}
        onUseExisting={openExistingRecord}
        onCancel={() => setBrandDraft(null)}
      />

      {sameAs && (
        <SameAsDialog
          key={sameAs.row.id}
          row={sameAs.row}
          initialKey={sameAs.initialKey}
          advertisers={registry}
          loading={registryLoading}
          loadError={registryError}
          onRetryLoad={loadRegistry}
          logoByKey={logoByKey}
          saving={linking}
          error={linkError}
          onLink={linkAsAlias}
          onClose={() => setSameAs(null)}
        />
      )}

      <NewAdvertiserDialog
        state={addTarget ? { kind: 'needsAdvertiser', needs: addTarget.needs } : null}
        categories={addTarget?.categories ?? []}
        agencies={addTarget?.agencies ?? []}
        eyebrow="Brand logos · Add to registry"
        intro={
          addTarget ? (
            <>
              Creator reports show “{addTarget.row.name}”, and no advertiser in the Sales registry matches it. Add it so it can have
              a logo. Confirm the industry category too — Sales uses it for rate cards and suggested rates.
            </>
          ) : undefined
        }
        submitLabel="Add to registry"
        editableName
        suggestion={addTarget && canAlias ? suggestedRecordOf(addTarget.row) ?? undefined : undefined}
        onCreated={recordCreated}
        onUseExisting={linkExistingRecord}
        onCancel={() => setAddTarget(null)}
      />

      <ConfirmDialog
        isOpen={!!removeTarget}
        title={`Remove the ${removeTarget ? recordNameOf(removeTarget.row) : ''} logo?`}
        message={
          removeTarget
            ? `Creator report pages go back to initials within about a minute${
                namesLinkedTo(sortedRows, removeTarget.key).length > 1
                  ? `, for every name linked to this advertiser (${namesLinkedTo(sortedRows, removeTarget.key).join(', ')})`
                  : ''
              }. You can pick or upload a logo again any time.`
            : ''
        }
        confirmText="Remove logo"
        variant="danger"
        onConfirm={runRemove}
        onCancel={() => setRemoveTarget(null)}
      />
    </div>
  );
};

export default BrandLogosTab;
